import { useContext, useEffect, useMemo, useState, MouseEvent } from 'react';
import styles from './main.module.scss';
import { Alert, Button, Checkbox, Col, Divider, Drawer, Form, Image, Input, message, Row, Spin } from 'antd';
import { useNavigate, useParams } from 'react-router-dom';
import CustomUploadComp from '../../upload/upload.index';
import { GlobalContext } from 'context';
import {
    APPlicationStoreEnum,
    CategoryFieldType,
    CategoryType,
    LocationType,
    PaginationType,
    PaymentTypeEnum,
    PostType,
} from 'types';
import AdMapComp from '../../map/map.index';
import dynamic from 'next/dynamic';
import SocialMediaComp from '../../socialMedia/socialMedia.index';
import { COLORS } from 'utilities';
import { AppStorage, Commafy, RestAPI, useAsyncClick, usePopstateBlocker, useRealTimeState } from 'scripts';
import { API, PATH } from 'data';
import CategoryFieldValuesComp from '../../categoryFieldValues/categoryFieldValues.index';
import { MobileHeaderComp, ConfirmLeaveModalComp } from 'components';
import { __ConvertCFVForAPI } from '../../categoryFieldValues/categoryFieldValues.script';
import { TbCreditCard, TbEdit } from 'react-icons/tb';
import { useRouter } from 'next/navigation';
import { __UpdateLastPostLocation } from '../../addPost.script';

function PostFormMainComp(props: { data?: PostType<'detail'> }) {
    const nav = useNavigate();
    const Map = useMemo(
        () =>
            dynamic(() => import('components/locationPicker/locationPicker.index'), {
                loading: () => (
                    <div className={`w-100 pv-9 center-content flex-1`}>
                        <Spin />
                    </div>
                ),
                ssr: false,
            }),
        [],
    );

    const { profile } = useContext(GlobalContext);
    const [uploading, setUploading] = useState(false);
    const [hasImages, setHasImages] = useState(false);
    const [previewOpen, setPreviewOpen] = useState(false);
    const [previewImage, setPreviewImage] = useState('');
    const { category, location, slug } = useParams();
    const [categoryData, setCategoryData] = useState<CategoryType<'detail'> | CategoryType<'list'>>();
    const [locationData, setLocationData] = useState<LocationType<'detail' | LocationType<'list'>>>();
    const [categoryFields, setCategoryFields] = useState<CategoryFieldType<'detail'>[]>();
    const [paymentModal, setPaymentModal] = useState<number>();
    const isDirty = useRealTimeState(false);
    const router = useRouter();
    const [confirmLeaveLink, setConfirmLeaveLink] = useState<string>();
    const { allowBack, blockBack, comp } = usePopstateBlocker();
    const lastPostLocation = AppStorage.lastPostLocation.get();

    const [form] = Form.useForm();

    useEffect(() => {
        if (props.data) {
            setCategoryData(props.data.category);
            setLocationData(props.data.locations?.at(0));
        } else {
            Promise.all([
                RestAPI.get<CategoryType<'detail'>>(API.category.detail(category ?? '')),
                RestAPI.get<LocationType<'detail'>>(API.location.detail(location ?? '')),
            ]).then(([categoryRes, locationRes]) => {
                setCategoryData(categoryRes.data);
                setLocationData(locationRes.data);
            });
        }
    }, [props.data]);

    useEffect(() => {
        setCategoryFields(undefined);
        if (categoryData) {
            RestAPI.get<PaginationType<CategoryFieldType<'detail'>>>(API.categoryField.list, {
                category_ancestors: categoryData.id,
                page_size: 25,
            }).then((res) => setCategoryFields(res.data.data));
        }
    }, [categoryData]);

    const handleOverlayClick = (e: MouseEvent<HTMLDivElement>) => {
        const elements = document.elementsFromPoint(e.clientX, e.clientY);
        const clickedButton = elements.find((el) =>
            ['bn-next-home', 'bn-next-catg', 'bn-panel-post', 'bn-panel-chat', 'bn-panel-prof'].includes(el.id),
        ) as HTMLButtonElement | undefined;
        if (clickedButton) {
            if (isDirty.get()) {
                const href = clickedButton.parentElement?.getAttribute('href');
                if (href?.length) setConfirmLeaveLink(href);
            }
        }
    };

    const submitHandler = () => {
        return new Promise<{ id: number; slug: string }>((resolve, reject) => {
            const values = form.getFieldsValue();
            const request = slug ? RestAPI.put : RestAPI.post;
            const url = slug ? API.posts.update(slug) : API.posts.create;
            request<PostType<'detail'>>(url, {
                ...values,
                gallery: values.gallery?.filter((i: number) => i !== 0),
                category_fields_values: undefined,
                approval_status: 'pending',
                category: categoryData?.id,
                locations: props.data ? props.data.locations?.map((i) => i.id) : [locationData?.id],
            })
                .then((res) => {
                    __UpdateLastPostLocation({
                        ...values,
                        locations: props.data ? props.data.locations : [locationData],
                    });
                    RestAPI.post(
                        API.categoryField.createBulkValues,
                        __ConvertCFVForAPI(values.category_fields_values, res.data.slug, categoryFields),
                    )
                        .then(() => {
                            allowBack();
                            isDirty.set(false);
                            message.success(
                                `${slug ? 'ویرایش' : 'ساخت'} آگهی با موفقیت انجام شد و پس از تایید مدیریت در لیست آگهی های تیک سبز قرار خواهد گرفت.`,
                            );
                            resolve({ id: res.data.id, slug: res.data.slug });
                            if (slug) nav(-1);
                        })
                        .catch(() => {
                            message.error(
                                `فرآیند ${slug ? 'ویرایش' : 'ساخت'} آگهی با خطا مواجه شد. لطفا بعدا تلاش کنید.`,
                            );
                            reject();
                        });
                })
                .catch((e) => {
                    if (JSON.stringify(e).includes('This object can only be updated three times a day.'))
                        message.error(
                            `شما امکان ویرایش آگهی بیش از ۳ مرتبه در روز را ندارید. لطفا تا پایان محدودیت ویرایش آگهی صبر کنید و یا از با پشتیبانی ارتباط بگیرید`,
                        );
                    else message.error(`فرآیند ${slug ? 'ویرایش' : 'ساخت'} آگهی با خطا مواجه شد. لطفا بعدا تلاش کنید.`);
                    reject();
                });
        });
    };

    const onPaymentHandler = useAsyncClick(() => {
        return new Promise((resolve, reject) => {
            submitHandler().then((post) => {
                if (AppStorage.applicationStore.get() === APPlicationStoreEnum.cafebazar) {
                    onBazarPaymentHandler(post)
                        .then(() => resolve(true))
                        .catch(() => reject());
                } else {
                    onZarinPalPaymentHandler(post)
                        .then(() => resolve(true))
                        .catch(() => reject());
                }
            });
        });
    });

    const onZarinPalPaymentHandler = (post: { id: number; slug: string }) => {
        return new Promise(() => {
            RestAPI.post<{ url: string }>(API.payment.pay, {
                type: PaymentTypeEnum.create,
                post: post.id,
            })
                .then((res) => (window.location.href = res.data.url))
                .catch(() => {
                    message.error('ورود به درگاه پرداخت با خطا مواجه شد. لطفا بعدا تلاش کنید');
                    nav(PATH.panel.myAds.edit(post.slug));
                });
        });
    };

    const onBazarPaymentHandler = (post: { id: number; slug: string }) => {
        return new Promise(() => {
            RestAPI.post<{ payment_url: string }>(API.payment.bazarPay, {
                type: PaymentTypeEnum.create,
                post: post.id,
            })
                .then((res) => (window.location.href = res.data.payment_url))
                .catch(() => {
                    message.error('ورود به درگاه پرداخت با خطا مواجه شد. لطفا بعدا تلاش کنید');
                    nav(PATH.panel.myAds.edit(post.slug));
                });
        });
    };

    const finishHandler = useAsyncClick(() => {
        const values = form.getFieldsValue();
        if (categoryFields === undefined) {
            message.error(`خطا در بارگذاری مشخصات فنی. لطفا مشخصات فنی را تکمیل فرمایید`);
            return;
        }
        if (!values.chatable && !values.has_phone) {
            message.error(`آگهی شما بایستی یکی از قابلیت های چت و یا تماس را داشته باشد`);
            return;
        }
        if (!values.gallery?.filter((i: number) => i !== 0).length) {
            message.error(`آگهی شما بایستی حداقل یک فایل تصویری داشته باشد`);
            return;
        }
        if (!hasImages) {
            message.error(`آگهی شما بایستی حداقل یک عکس داشته باشد`);
            return;
        }
        if (uploading) {
            message.error(`فایل مورد نظر شما در حال آپلود میباشد. لطفا صبر کنید`);
            return;
        }
        if (slug) return submitHandler();
        else if (categoryData && locationData) {
            allowBack();
            isDirty.set(false);
            setPaymentModal(categoryData.price * (locationData.coefficient ?? 1));
        }
    });

    const uniqueClassGen = (name: string) => {
        return `form-item-id-${name}`;
    };

    const resetForm = () => {
        form.resetFields();
    };

    if (categoryFields === undefined || locationData === undefined || categoryData === undefined)
        return (
            <div className={`w-100 pv-9 center-content flex-1`}>
                <Spin />
            </div>
        );

    return (
        <>
            <Form
                form={form}
                layout="vertical"
                requiredMark={false}
                onFinish={finishHandler.onClick}
                onFinishFailed={(e) => {
                    const id = uniqueClassGen(e.errorFields.at(0)?.name?.toString() ?? '');
                    if (id?.length) {
                        const element = document.querySelector('.' + id.toString());
                        if (element) {
                            element.scrollIntoView({ behavior: 'smooth', block: 'center' });
                        }
                    }
                }}
                onFieldsChange={(e) => {
                    if (e.some((i) => i.touched)) {
                        blockBack();
                        isDirty.set(true);
                    } else {
                        allowBack();
                        isDirty.set(false);
                    }
                }}
                // onValuesChange={() => formValues.set(form.getFieldsValue())}
            >
                <MobileHeaderComp
                    title={props.data ? 'ویرایش آگهی' : 'ثبت آگهی'}
                    backPath={props.data ? PATH.panel.myAds.detail(props.data.slug) : undefined}
                    onIconClick={!props.data ? () => nav(-2) : undefined}
                    // onIconClick={step === 2 ? () => setStep(1) : undefined}
                    // help={
                    //     <h3 className="t-p3 t-primary-i t-400 center-content g-1 pointer">
                    //         <IoIosHelpCircleOutline size={20} color={COLORS.primary} /> راهنمای ثبت آگهی
                    //     </h3>
                    // }
                />
                <div className={`${styles['form-container']}`}>
                    {props.data ? null : (
                        <Alert
                            className="mb-3"
                            message={
                                <div>
                                    <p>دسته بندی: {categoryData.title}</p>
                                    <p>شهر: {locationData.title}</p>
                                </div>
                            }
                            onClick={() => nav(PATH.panel.addPost.index())}
                            closeIcon={<TbEdit size={20} />}
                            type="warning"
                        />
                    )}
                    <Row gutter={24}>
                        <Col lg={{ span: 24 }}>
                            <h3 className="t-h2 t-l-black1 t-500 mb-2">تصاویر آگهی</h3>
                        </Col>
                        <Col lg={{ span: 24 }} md={{ span: 24 }} xs={{ span: 24 }}>
                            {previewImage && (
                                <Image
                                    alt=""
                                    wrapperStyle={{ display: 'none' }}
                                    preview={{
                                        visible: previewOpen,
                                        onVisibleChange: (visible) => setPreviewOpen(visible),
                                        afterOpenChange: (visible) => !visible && setPreviewImage(''),
                                    }}
                                    src={previewImage}
                                />
                            )}
                            <Form.Item
                                name={'gallery'}
                                initialValue={props.data?.gallery?.map((i) => i.id) ?? []}
                                className={uniqueClassGen('gallery')}
                            >
                                <CustomUploadComp
                                    initialValue={props.data?.gallery ?? []}
                                    uploadingChange={setUploading}
                                    onHasImagesChange={setHasImages}
                                    uploading={uploading}
                                />
                            </Form.Item>
                        </Col>
                        <Col lg={{ span: 24 }} md={{ span: 24 }} xs={{ span: 24 }}>
                            <Form.Item
                                name={'title'}
                                label={<p className="t-h3 t-400 t-l-black1">عنوان آگهی</p>}
                                rules={[{ required: true, message: <span>لطفا عنوان آگهی خود را وارد کنید!</span> }]}
                                initialValue={props.data?.title}
                                className={uniqueClassGen('title')}
                            >
                                <Input placeholder="عنوان آگهی خود را بنویسید..." showCount maxLength={50} />
                            </Form.Item>
                        </Col>
                        <Col lg={{ span: 24 }} md={{ span: 24 }} xs={{ span: 24 }}>
                            <Form.Item
                                name={'description'}
                                label={<p className="t-h3 t-400 t-l-black1">{'توضیحات آگهی'}</p>}
                                rules={[{ required: true, message: <span>لطفا توضیحات را وارد کنید!</span> }]}
                                initialValue={props.data?.description}
                                className={uniqueClassGen('description')}
                            >
                                <Input.TextArea color={COLORS.black} placeholder="توضیحات آگهی خود را بنویسید..." />
                            </Form.Item>
                        </Col>
                        <Col lg={{ span: 24 }} md={{ span: 24 }} xs={{ span: 24 }}>
                            <Form.Item
                                name="latitude"
                                className="d-none"
                                initialValue={props.data?.latitude ?? locationData.latitude}
                            />
                            <Form.Item
                                name="longitude"
                                className="d-none"
                                initialValue={props.data?.longitude ?? locationData.longitude}
                            />
                            <AdMapComp
                                form={form}
                                Map={Map}
                                initialValue={
                                    props.data?.latitude && props.data?.longitude
                                        ? [props.data.latitude, props.data.longitude]
                                        : !props.data &&
                                            locationData.id === lastPostLocation?.location?.id &&
                                            lastPostLocation.latitude &&
                                            lastPostLocation.longitude
                                          ? [lastPostLocation.latitude, lastPostLocation.longitude]
                                          : locationData.latitude && locationData.longitude
                                            ? [locationData.latitude, locationData.longitude]
                                            : undefined
                                }
                            />
                        </Col>
                        <Col lg={{ span: 24 }} md={{ span: 24 }} xs={{ span: 24 }}>
                            <Form.Item
                                name="address"
                                label={<p className="t-h3 t-400 t-l-black1">{'آدرس'}</p>}
                                rules={[{ required: true, message: <span>لطفا آدرس را وارد کنید!</span> }]}
                                initialValue={
                                    props.data?.address ??
                                    (locationData.id === lastPostLocation?.location?.id && !props.data
                                        ? lastPostLocation.address
                                        : undefined)
                                }
                                className={uniqueClassGen('address')}
                            >
                                <Input.TextArea color={COLORS.black} placeholder="آدرس آگهی خود را بنویسید..." />
                            </Form.Item>
                        </Col>
                        <Col lg={{ span: 24 }} md={{ span: 24 }} xs={{ span: 24 }}>
                            <Form.Item
                                name={'chatable'}
                                label={<p className="t-h3 t-400 t-l-black1">{'چت'}</p>}
                                initialValue={props.data?.chatable}
                                valuePropName="checked"
                                className={uniqueClassGen('chatable')}
                            >
                                <Checkbox>
                                    <p>چت برای این آگهی فعال است</p>
                                </Checkbox>
                            </Form.Item>
                        </Col>
                        <Col lg={{ span: 24 }} md={{ span: 24 }} xs={{ span: 24 }}>
                            <Form.Item
                                name={'has_phone'}
                                label={'تماس با ' + profile?.phone}
                                initialValue={props.data?.has_phone}
                                valuePropName="checked"
                                className={uniqueClassGen('has_phone')}
                            >
                                <Checkbox>
                                    <p>قابلیت تماس برای این آگهی فعال است</p>
                                </Checkbox>
                            </Form.Item>
                        </Col>
                        {categoryFields.length ? (
                            <>
                                <Divider />
                                <Col
                                    lg={{ span: 24 }}
                                    md={{ span: 24 }}
                                    xs={{ span: 24 }}
                                    className={uniqueClassGen('category_fields_values')}
                                >
                                    {/* <Form.Item name={'category_fields_values'}> */}
                                    <CategoryFieldValuesComp
                                        categoryFields={categoryFields}
                                        initialData={props.data?.category_fields_values}
                                    />
                                    {/* </Form.Item> */}
                                </Col>
                            </>
                        ) : null}
                        <Divider />
                        <Col lg={{ span: 24 }} md={{ span: 24 }} xs={{ span: 24 }}>
                            <SocialMediaComp initialData={props.data?.socials} />
                        </Col>
                    </Row>
                    {isDirty.dep ? <div className={styles['confirm-cover']} onClick={handleOverlayClick} /> : null}
                </div>
                <Alert
                    className="mt-5 bg-d-bgDark"
                    message="شرایط ثبت آگهی در سایت تیک سبز"
                    description={
                        <div className="p-3">
                            <p className="t-h4">
                                ۱- تصاویر آگهی باید واضح، واقعی و با کیفیت مناسب باشند و تصویر اول فاقد هرگونه
                                نوشته،ایموجی یا متن اضافه باشد
                            </p>
                            <p className="t-h4">۲- درج قیمت محصول الزامی است.</p>
                            <p className="t-h4">
                                ۳- آدرس فروشگاه، کارگاه، تولیدی یا محل ارائه خدمات باید به‌صورت کامل و دقیق در متن آگهی
                                درج شود.
                            </p>
                            <p className="t-h4">
                                ۴- استفاده از عبارات تبلیغاتی اغراق‌آمیز در عنوان آگهی مانند «حراجی»، «تخفیف ویژه»، «زیر
                                قیمت بازار» و موارد مشابه مجاز نیست.
                            </p>
                            <p className="t-h4">
                                ۵- ثبت آگهی مربوط به کالاهای شخصی یا دست‌دوم در تیک سبز مجاز نیست. این پلتفرم صرفاً برای
                                معرفی فروشگاه‌ها، تولیدکنندگان و کسب‌وکارها طراحی شده است.
                            </p>
                            <p className="t-h4">
                                ۶- ثبت آگهی مواد غذایی دست‌ساز یا خانگی، یا محصولاتی که فاقد تاریخ مصرف، مجوز یا مشخصات
                                استاندارد هستند مجاز نیست.
                            </p>
                            <p className="t-h4">
                                ۷- ثبت آگهی مربوط به انواع قرص‌ها، داروهای شیمیایی یا گیاهی، مکمل‌های چاقی و لاغری،
                                افزایش قد، درمان زگیل و سایر محصولات دارویی یا شبه‌دارویی ممنوع است.
                            </p>
                            <ul className="pt-2">
                                <li>
                                    مسئولیت صحت اطلاعات، متن آگهی، تصاویر، قیمت و مشخصات درج‌شده به‌طور کامل بر عهده
                                    آگهی‌دهنده است. و پلتفرم هیچ مسئولیتی در قبال آگهی شما ندارد.
                                </li>
                                <li>
                                    با ثبت آگهی در پلتفرم تیک سبز، شما تأیید می‌کنید که تمامی قوانین فوق را مطالعه کرده
                                    و پذیرفته‌اید.
                                </li>
                            </ul>
                        </div>
                    }
                    type="warning"
                />
                <div className={`${styles['btn-in-mobile']} g-2 center-content-y d-md-none`}>
                    <Button className="w-100" htmlType="reset" onClick={() => resetForm()}>
                        {props.data ? 'تنظیم مجدد فرم' : 'پاکسازی فرم'}
                    </Button>
                    <Button className="w-100" htmlType={'submit'} loading={finishHandler.loading}>
                        {props.data ? 'ویرایش آگهی' : 'پرداخت امن'}
                    </Button>
                </div>
            </Form>
            <Drawer
                open={paymentModal !== undefined}
                onClose={() => setPaymentModal(undefined)}
                closeIcon={null}
                height={'auto'}
                placement="bottom"
                title={
                    <div className="align-items-center g-1">
                        <TbCreditCard className="t-l-black1 t-d-gray2" size={24} />
                        پرداخت هزینه آگهی
                    </div>
                }
            >
                <p>
                    هزینه انتشار آگهی شما توسط تیک سبز که بر اساس دسته‌بندی و موقعیت‌های مورد نظرتان محاسبه شده است
                    برابر است با :
                </p>
                <h4 className="mt-2 t-500 t-gray2-i mb-2">
                    {paymentModal ? Commafy(paymentModal / 10) : ''} تومان برای{' '}
                    <span className="t-primary">یک سال</span>
                </h4>
                <p className="t-l-black1 t-400 mb-2">
                    آگهی شما پس از پرداخت و تایید توسط مدیریت به مدت ۱ سال در لیست آگهی های تیک سبز قرار خواهد گرفت.
                </p>
                <Alert
                    className="mv-3 bg-d-bgDark"
                    message="لطفا پیش از ورود به درگاه پرداخت از خاموش بودن فیلترشکن خود اطمینان حاصل کنید"
                    type="warning"
                    showIcon
                />
                <div className="d-flex gap-16">
                    <Button className="flex-1" type="primary" onClick={() => setPaymentModal(undefined)}>
                        لغو
                    </Button>
                    <Button
                        className="flex-1"
                        type="default"
                        loading={onPaymentHandler.loading}
                        onClick={onPaymentHandler.onClick}
                    >
                        ورود به صفحه پرداخت
                    </Button>
                </div>
            </Drawer>
            {comp}
            <ConfirmLeaveModalComp
                open={confirmLeaveLink !== undefined}
                onCancel={() => setConfirmLeaveLink(undefined)}
                onConfirm={() => {
                    if (confirmLeaveLink?.includes('panel')) nav(confirmLeaveLink);
                    else if (confirmLeaveLink?.length) router.push(confirmLeaveLink);
                }}
            />
        </>
    );
}

export default PostFormMainComp;
